import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Stethoscope, User, Search, Plus, Pill, Lock } from "lucide-react";
import VerifiedBadge from "@/components/app/VerifiedBadge";

const PRO_ROLES = ["doctor", "pharmacy"];

function ChannelIcon({ role, size = 12 }) {
  if (role === "doctor") return <Stethoscope className="text-[#618DB8]" style={{ width: size, height: size }} />;
  if (role === "pharmacy") return <Pill className="text-emerald-600" style={{ width: size, height: size }} />;
  return <User className="text-[#FF7A5C]" style={{ width: size, height: size }} />;
}

function channelLabel(role) {
  if (role === "doctor") return "Praticien";
  if (role === "pharmacy") return "Pharmacie";
  return "Patient";
}

export default function Messages() {
  const { user } = useAuth();
  const isPro = PRO_ROLES.includes(user?.role);

  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [tab, setTab] = useState("all"); // all | peers | patients
  const [search, setSearch] = useState("");
  const endRef = useRef(null);

  // New conversation dialog
  const [openNew, setOpenNew] = useState(false);
  const [pickerTab, setPickerTab] = useState(isPro ? "peers" : "doctors"); // peers | patients (doctor), doctors (patient)
  const [pickerQ, setPickerQ] = useState("");
  const [directory, setDirectory] = useState([]);

  const loadConvs = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConvs(data);
      if (!active && data.length > 0) setActive(data[0]);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const loadMessages = async (conv) => {
    if (!conv) return;
    try {
      const { data } = await api.get(`/messages/${conv.conversation_id}`);
      setMessages(data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { loadConvs(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadMessages(active); }, [active]);

  // Counters
  const counts = useMemo(() => {
    let peers = 0, patients = 0;
    for (const c of convs) {
      if (PRO_ROLES.includes(c.other_user?.role)) peers += 1;
      else patients += 1;
    }
    return { peers, patients, all: convs.length };
  }, [convs]);

  const filtered = useMemo(() => {
    let list = convs;
    if (tab === "peers") list = list.filter((c) => PRO_ROLES.includes(c.other_user?.role));
    else if (tab === "patients") list = list.filter((c) => c.other_user?.role === "patient");
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((c) => (c.other_user?.name || "").toLowerCase().includes(s) || (c.other_user?.email || "").toLowerCase().includes(s));
    }
    return list;
  }, [convs, tab, search]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      await api.post("/messages", { conversation_id: active.conversation_id, content: text });
      setText("");
      loadMessages(active);
      loadConvs();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const loadDirectory = async () => {
    try {
      if (pickerTab === "peers" || pickerTab === "doctors") {
        const { data } = await api.get("/doctors/search", { params: { q: pickerQ } });
        setDirectory(data.filter((d) => d.user_id !== user.user_id));
      } else if (pickerTab === "patients") {
        const { data } = await api.get("/patients");
        const q = pickerQ.toLowerCase();
        setDirectory(q ? data.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q)) : data);
      }
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { if (openNew) loadDirectory(); /* eslint-disable-next-line */ }, [openNew, pickerTab, pickerQ]);

  const startConv = async (targetUserId) => {
    try {
      const { data: conv } = await api.post("/conversations", { participant_id: targetUserId });
      await loadConvs();
      setActive(conv);
      setOpenNew(false);
      // Switch to matching tab
      const target = directory.find((d) => d.user_id === targetUserId);
      if (target) setTab(PRO_ROLES.includes(target.role) ? (isPro ? "peers" : "all") : "patients");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const otherIsPro = active && PRO_ROLES.includes(active.other_user?.role);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto h-[calc(100vh-2rem)] flex flex-col">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-2">Messagerie chiffrée</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-deep tracking-tight">Conversations</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isPro
              ? "Échangez séparément avec vos confrères et vos patients."
              : "Discutez avec vos praticiens et pharmacies en toute sécurité."}
          </p>
        </div>

        {/* Bandeau permanent chiffrement E2E · HDS — US-21 CDC v1.0 + §5 conformité UX */}
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 text-xs font-semibold text-emerald-800" data-testid="encryption-banner" role="status">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" />
          <span>Chiffrement de bout en bout actif · Hébergement HDS France</span>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button className="btn-coral" data-testid="new-conv-btn"><Plus className="h-4 w-4 me-1" />Nouvelle conversation</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Démarrer une conversation</DialogTitle></DialogHeader>
            <div className="mt-2">
              <div className="flex gap-2 mb-3">
                {isPro ? (
                  <>
                    <button onClick={() => setPickerTab("peers")} className={`text-xs px-3 py-1.5 rounded-full font-semibold ${pickerTab === "peers" ? "bg-[#618DB8] text-white" : "bg-slate-100 text-slate-600"}`} data-testid="picker-tab-peers"><Stethoscope className="h-3 w-3 inline me-1" />Confrères</button>
                    <button onClick={() => setPickerTab("patients")} className={`text-xs px-3 py-1.5 rounded-full font-semibold ${pickerTab === "patients" ? "bg-[#FF7A5C] text-white" : "bg-slate-100 text-slate-600"}`} data-testid="picker-tab-patients"><User className="h-3 w-3 inline me-1" />Mes patients</button>
                  </>
                ) : (
                  <button onClick={() => setPickerTab("doctors")} className="text-xs px-3 py-1.5 rounded-full font-semibold bg-[#618DB8] text-white"><Stethoscope className="h-3 w-3 inline me-1" />Praticiens</button>
                )}
              </div>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={pickerQ} onChange={(e) => setPickerQ(e.target.value)} placeholder="Rechercher par nom ou email…" className="pl-9" data-testid="picker-search" />
              </div>
              <div className="mt-3 max-h-72 overflow-y-auto divide-y divide-slate-100 border border-slate-100 rounded-lg">
                {directory.length === 0 ? (
                  <div className="text-center text-xs text-slate-400 py-8">
                    {pickerTab === "patients" ? "Aucun patient dans votre cabinet pour l'instant." : "Aucun résultat."}
                  </div>
                ) : directory.map((u) => (
                  <button key={u.user_id} onClick={() => startConv(u.user_id)} className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-3" data-testid={`pick-${u.user_id}`}>
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#618DB8] to-[#2A4365] text-white flex items-center justify-center text-xs font-bold">
                      {(u.name || "?").replace(/^Dr\s+/, "").charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm flex items-center gap-1">{u.name}{u.blue_check && <VerifiedBadge size={11} />}</div>
                      <div className="text-xs text-slate-500 truncate">{u.specialty || u.email}</div>
                    </div>
                    <ChannelIcon role={u.role} size={14} />
                  </button>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 bg-white border border-slate-200 rounded-2xl overflow-hidden min-h-0 shadow-sm">
        <aside className="border-r border-slate-200 flex flex-col min-h-0">          {/* Tabs */}
          {isPro && (
            <div className="px-3 pt-3 pb-2 border-b border-slate-100 flex gap-1.5 items-center">
              <button onClick={() => setTab("all")} className={`text-xs px-2.5 py-1.5 rounded-full font-semibold ${tab === "all" ? "bg-deep text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="tab-all">
                Tous · {counts.all}
              </button>
              <button onClick={() => setTab("peers")} className={`text-xs px-2.5 py-1.5 rounded-full font-semibold flex items-center gap-1 ${tab === "peers" ? "bg-[#618DB8] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="tab-peers">
                <Stethoscope className="h-3 w-3" />Confrères · {counts.peers}
              </button>
              <button onClick={() => setTab("patients")} className={`text-xs px-2.5 py-1.5 rounded-full font-semibold flex items-center gap-1 ${tab === "patients" ? "bg-[#FF7A5C] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="tab-patients">
                <User className="h-3 w-3" />Patients · {counts.patients}
              </button>
            </div>
          )}
          {/* Search */}
          <div className="px-3 py-2 border-b border-slate-100 relative">
            <Search className="h-4 w-4 absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="pl-9 h-9 text-sm" data-testid="conv-search" />
          </div>

          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center">
                {convs.length === 0 ? "Aucune conversation. Démarrez-en une." : "Aucun résultat dans ce filtre."}
              </div>
            ) : filtered.map((c) => {
              const pro = PRO_ROLES.includes(c.other_user?.role);
              return (
                <button
                  key={c.conversation_id}
                  onClick={() => setActive(c)}
                  data-testid={`conv-${c.conversation_id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition flex items-start gap-3 ${active?.conversation_id === c.conversation_id ? "bg-[#618DB8]/5" : ""}`}
                >
                  <div className="relative shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white ${pro ? "bg-gradient-to-br from-[#618DB8] to-[#2A4365]" : "bg-gradient-to-br from-[#FF7A5C] to-[#e0593c]"}`}>
                      {(c.other_user?.name || "?").replace(/^Dr\s+/, "").charAt(0)}
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full p-0.5 border border-slate-200">
                      <ChannelIcon role={c.other_user?.role} size={10} />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="font-semibold text-sm truncate">{c.other_user?.name || "—"}</div>
                      {c.other_user?.blue_check && <VerifiedBadge size={11} />}
                    </div>
                    <div className="text-[11px] text-slate-400">{channelLabel(c.other_user?.role)}{c.other_user?.specialty ? ` · ${c.other_user.specialty}` : ""}</div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">
                      {c.last_message?.content || "Démarrer la conversation…"}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="md:col-span-2 flex flex-col min-h-0">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-sm gap-2">
              <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center"><Send className="h-7 w-7" /></div>
              Sélectionnez ou démarrez une conversation.
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
                <div className="relative">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white ${otherIsPro ? "bg-gradient-to-br from-[#618DB8] to-[#2A4365]" : "bg-gradient-to-br from-[#FF7A5C] to-[#e0593c]"}`}>
                    {(active.other_user?.name || "?").replace(/^Dr\s+/, "").charAt(0)}
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full p-0.5 border border-slate-200">
                    <ChannelIcon role={active.other_user?.role} size={10} />
                  </span>
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm flex items-center gap-1 truncate">
                    {active.other_user?.name}
                    {active.other_user?.blue_check && <VerifiedBadge size={12} />}
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1">
                    <span className={`px-1.5 py-0.5 rounded-full font-semibold ${otherIsPro ? "bg-[#618DB8]/10 text-[#2A4365]" : "bg-[#FF7A5C]/10 text-[#a8421f]"}`}>
                      {otherIsPro ? "Canal confrère" : "Canal patient"}
                    </span>
                    {active.other_user?.specialty && <span>· {active.other_user.specialty}</span>}
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50">
                {messages.length === 0 ? (
                  <div className="text-center text-xs text-slate-400 py-10">Aucun message pour l'instant. Soyez le premier à écrire.</div>
                ) : messages.map((m) => {
                  const mine = m.sender_id === user.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm shadow-sm ${mine ? (otherIsPro ? "bg-[#618DB8] text-white" : "bg-[#FF7A5C] text-white") : "bg-white border border-slate-200"}`}>
                        <div className="whitespace-pre-wrap">{m.content}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/80" : "text-slate-400"}`}>
                          {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="border-t border-slate-200 p-3 flex items-center gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={otherIsPro ? "Message confrère…" : "Message patient…"} className="flex-1 h-10" data-testid="message-input" />
                <Button type="submit" className={otherIsPro ? "btn-primary" : "btn-coral"} data-testid="send-message-btn"><Send className="h-4 w-4" /></Button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
